import React, {Fragment, useEffect} from 'react';

import clsx from 'clsx';
import {useHistory} from 'react-router-dom';
import {CallMade, Add, CheckSharp} from '@material-ui/icons';
import {AppBar, Box, Divider, Grid, Typography} from '@material-ui/core';

import {EfeAccordion, EfeButtonPrimary, EfeButtonSecondary} from 'components';
import {EfeResponse, EfeViewsArray, ShareholdersResponse} from 'commons/models';
import {getRandomHex, nextStep, useViewSearch} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {AppBarStyles} from 'assets/tss/commons';
import {MainContent} from 'layouts/commons';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {ShareholderInfo} from 'commons/types/Types';
import {HistoryRoutes, Messages, PersonTypes, ViewChildId} from 'commons/constants';
import {ClientService} from 'services';
import {useSpinner} from 'hooks';

function Shareholders() {
    const classes = AppBarStyles();
    const {menu, fatherKycId, setKycId} = useMenuContext();
    const history = useHistory();
    const {flow, moduleStep, updateModuleStep} = useFlowContext();
    const [showViews] = React.useState<{value: boolean}>({value: false});
    const [aux, setAux] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [viewKey, setViewKey] = React.useState<string>(
        `View-${getRandomHex()}`
    );
    const {
        percentageCompleted,
        shareholders,
        isTempShareholderActive,
        setTempShaholderActive,

        setShareholders,
        setPercentageCompleted,
        shareholderPersonType,
    } = useRelatedPeopleContext();
    const views: Array<EfeViewsArray> = useViewSearch({...flow}, [...menu]);

    function handleSubmit() {
        const path = nextStep(flow, menu, true);
        setKycId(-1);
        updateModuleStep(false);
        history.push(path);
    }

    async function handleAddShareholder() {
        await setKycId(-1);
        showViews.value = true;
        setAux(!aux);
        history.push(HistoryRoutes.SHAREHOLDER_INFO);
        setViewKey(`View-${getRandomHex()}`);
        setTempShaholderActive(false);
    }

    useEffect(() => {
        if (shareholders && shareholders.length && !isTempShareholderActive) {
            showViews.value = false;
            setViewKey(`View-${getRandomHex()}`);
        }
    }, [shareholders, isTempShareholderActive]);

    useEffect(() => {
        if (fatherKycId > 0 && !isTempShareholderActive) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<ShareholdersResponse> =
                        await ClientService.getShareholders(fatherKycId);
                    if (response && response.data) {
                        const tmpRelateds: ShareholderInfo[] = [];
                        let tmpPercentage = 0.0;
                        showViews.value = false;
                        for (const item of response.data.data) {
                            tmpPercentage = tmpPercentage + (item.participationPercent || 0);
                            const name =
                                item.personTypeKey === PersonTypes.MORAL
                                    ? item.businessDescription
                                    : `${item.name} ${item.lastName} ${item.secondLastName}`;
                            tmpRelateds.push({
                                kycId: item.kycId || 0,
                                completeName: name || '',
                                participationPercentage: item.participationPercent || 0,
                                personTypeKey: item.personTypeKey || '',
                            });
                        }
                        updateModuleStep(tmpPercentage * 1 >= 100);
                        setShareholders(tmpRelateds);
                        setPercentageCompleted(tmpPercentage);
                    }
                    setIsLoading(false);
                } catch (error) {
                    updateModuleStep(true);
                    setIsLoading(false);
                    setShareholders([]);
                    setKycId(-1);
                }
            })();
        }
    }, []);

    function handleChange(kycIdSelected: number) {
        setKycId(kycIdSelected);
        showViews.value = true;
        setTempShaholderActive(true);
        history.push(HistoryRoutes.SHAREHOLDER_INFO);
    }

    return (
        <Fragment>
            {isLoading && useSpinner(isLoading)}
            <MainContent>
                <Grid container direction='row' justifyContent='space-between' spacing={5}>
                    <Grid item xs={12}>
                        <Box color='text.primary'>
                            <Typography variant='body2' color='textPrimary'>
                                {Messages.CAPTURE_THE_WHOLE_PERCENTAGE}
                            </Typography>
                        </Box>
                    </Grid>
                    {shareholders && shareholders.length > 0 && (
                        <Grid
                            item
                            xs={12}
                            className={clsx(
                                percentageCompleted === 100
                                    ? classes.snackBarSuccess
                                    : classes.snackBarError
                            )}>
                            <Box color='text.primary'>
                                <Typography variant='body2' color='textPrimary'>
                                    {`El porcentaje de participaci\u00F3n de los accionistas suma: ${percentageCompleted}%`}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                    {shareholders &&
                        shareholders.length > 0 &&
                        shareholders.map((item: ShareholderInfo) => {
                            return (
                                <Grid item xs={12} key={item.kycId}>
                                    <EfeAccordion
                                        values={[
                                            {
                                                title: `Accionista / ${item.completeName} / ${item.participationPercentage}%`,
                                                icon: <CheckSharp />,
                                                disabled: false,
                                                expandIcon: false,
                                                reactElement: '',
                                                expanded: false,
                                            },
                                        ]}
                                        onClick={() => {
                                            handleChange(item.kycId);
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                </Grid>
                <Divider variant='middle' />
            </MainContent>
            <Grid container direction='row' justifyContent='space-between' key={viewKey}>
                {(showViews.value || isTempShareholderActive) &&
                    views &&
                    views.length > 0 &&
                    views.map((view: EfeViewsArray) => {
                        if (
                            PersonTypes.MORAL !== shareholderPersonType &&
                            view.index.includes(ViewChildId.SHAREHOLDER_V_RL_INFORMATION_MORAL)
                        ) {
                            return <></>;
                        } else {
                            return (
                                <Grid item xs={12} key={view.index}>
                                    {view.view}
                                </Grid>
                            );
                        }
                    })}
            </Grid>

            <AppBar position='fixed' color='primary' className={classes.appBar}>
                <MainContent>
                    <Grid container direction='row' justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' flexDirection='row' justifyContent='center' m={2}>
                                <Grid item xs={12}>
                                    <Box m={2}>
                                        <EfeButtonSecondary
                                            fullWidth
                                            variant='outlined'
                                            onClick={handleAddShareholder}
                                            disabled={percentageCompleted >= 100}
                                            endIcon={<Add />}>
                                            {'Agregar accionista'}
                                        </EfeButtonSecondary>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box m={2}>
                                        <EfeButtonPrimary
                                            fullWidth
                                            onClick={handleSubmit}
                                            disabled={!moduleStep || percentageCompleted < 100}
                                            endIcon={<CallMade />}>
                                            {'Guardar y continuar'}
                                        </EfeButtonPrimary>
                                    </Box>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </MainContent>
            </AppBar>
        </Fragment>
    );
}

export default Shareholders;
